import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Spin, Tooltip, Row, Col } from 'antd';
import cx from 'classnames';

import {
  getWorkerDetail,
  getWorkerInfoFile,
  saveWorkerAsSeparate,
} from 'redux/reducers/workerSlice';
import { WorkerType } from './types';
import { getFullName } from 'utils/personUtils';
import WorkerGeneralInfo from './WorkerGeneralInfo';
import WorkerModal from 'components/modals/WorkerModal';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BaseCollapse from 'components/wrappers/BaseCollapse';
import BasePageCard from 'components/wrappers/BasePageCard';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import PotentialDuplicateWorker from './PotentialDuplicateWorker';
import { ResourceDownloadResponse } from 'api/backofficeApiClient';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import ActivitiesDetailsSection from 'components/ActivitiesDetailsSection';
import { ActivitiesSectionPageTypeEnum } from 'components/ActivitiesDetailsSection/enums';

import styles from './WorkerDetailsPage.module.less';

type ParamsProps = {
  workerKey: string;
};

export default function WorkerDetailsPage() {
  const { workerKey } = useParams<ParamsProps>();
  const dispatch = useAppDispatch();
  const [worker, setWorker] = useState<WorkerType>();
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [workerFileKeyInfo, setWorkerFileKeyInfo] =
    useState<ResourceDownloadResponse>();
  const containerRef = useRef<HTMLDivElement>(null);
  const isAdmin = useAppSelector(isAdminSelector);

  const loadWorkerFile = useCallback(
    async (workerId: string) => {
      const result = await dispatch(
        getWorkerInfoFile({
          workerId,
          isAdminRole: isAdmin as boolean,
        }),
      );

      if (getWorkerInfoFile.fulfilled.match(result)) {
        setWorkerFileKeyInfo(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [dispatch, isAdmin],
  );

  const loadWorkerDetails = useCallback(async () => {
    const result = await dispatch(
      getWorkerDetail({ workerKey, isAdminRole: isAdmin }),
    );

    if (getWorkerDetail.fulfilled.match(result)) {
      if (!!result.payload?.workerInfo?.workerFileKey) {
        await loadWorkerFile(result.payload?.id as string);
      } else {
        setWorkerFileKeyInfo(undefined);
      }

      setWorker(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsInitialLoaded(true);
  }, [dispatch, workerKey, isAdmin, loadWorkerFile]);

  useEffect(() => {
    loadWorkerDetails();
  }, [loadWorkerDetails]);

  const onCreateSeparateWorker = async () => {
    setIsButtonsDisabled(true);

    const result = await dispatch(saveWorkerAsSeparate(worker?.id as string));

    if (saveWorkerAsSeparate.fulfilled.match(result)) {
      await loadWorkerDetails();
      BaseAlert.success('Worker successfully marked as non-duplicate.');
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsButtonsDisabled(false);
  };

  if (!isInitialLoaded) return <Spin size="large" />;

  if (!worker) return null;

  const {
    creator,
    workerInfo,
    isDuplicated,
    duplicates,
    referredMembers,
    referredAssignments,
  } = worker;

  return (
    <>
      {isEditModalVisible && (
        <WorkerModal
          isEditMode
          worker={worker}
          onSuccess={loadWorkerDetails}
          workerInfoFile={workerFileKeyInfo}
          onClose={() => setIsEditModalVisible(false)}
        />
      )}

      <div ref={containerRef} className="page-wrapper scroll-box">
        <div className="page-holder">
          <BasePageLabel label="Worker details" creator={creator} />

          <div className="page-box page-inner">
            <div className="page-inner-container">
              <div className="page-head">
                <div className="page-head__left">
                  <div className="page-title page-head__title">
                    {getFullName(workerInfo?.firstName, workerInfo?.lastName)}
                  </div>
                </div>

                <div className="page-head__actions">
                  <div>
                    <Button
                      onClick={() => setIsEditModalVisible(true)}
                      className="with-icon mobile-icon-only"
                      size="large"
                      icon={<span className="icon icon-edit" />}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              </div>

              <Row gutter={[10, 10]}>
                <Col xs={24} lg={12} className="d-flex">
                  <Row gutter={[4, 4]}>
                    <Col xs={24} className="d-flex">
                      <BasePageCard
                        title={
                          isAdmin
                            ? 'Current worker record'
                            : 'General information'
                        }
                        subTitle={
                          isAdmin &&
                          isDuplicated && (
                            <Button
                              onClick={onCreateSeparateWorker}
                              loading={isButtonsDisabled}
                              size="large"
                            >
                              Create as separate worker
                            </Button>
                          )
                        }
                        icons={<span className="icon icon-writing-tool-user" />}
                        className="stretch-card"
                      >
                        <div className="content-wrapper">
                          <Row gutter={[4, 4]} className="titled-sheet">
                            <WorkerGeneralInfo
                              worker={worker}
                              workerFileKeyInfo={workerFileKeyInfo}
                            />
                          </Row>
                        </div>
                      </BasePageCard>
                    </Col>

                    {isAdmin &&
                      (!!referredMembers?.length ||
                        !!referredAssignments?.length) && (
                        <Col xs={24} className="d-flex">
                          <BasePageCard
                            title="Refers to"
                            icons={<span className="icon icon-refers-to" />}
                            className="stretch-card"
                          >
                            <div className="content-wrapper">
                              <Row gutter={[4, 4]} className="titled-sheet">
                                {!!referredMembers?.length && (
                                  <Col xs={24} md={6}>
                                    <div className="title">Members:</div>
                                    {referredMembers
                                      .map<React.ReactNode>((member) => (
                                        <Link
                                          to={`/member/${member.memberKey}`}
                                          key={`reffered-member-${member.memberKey}`}
                                        >
                                          {getFullName(
                                            member.firstName,
                                            member.lastName,
                                          )}
                                        </Link>
                                      ))
                                      .reduce((previous, current) => [
                                        previous,
                                        ', ',
                                        current,
                                      ])}
                                  </Col>
                                )}

                                {!!referredAssignments?.length && (
                                  <Col xs={24} md={6}>
                                    <div className="title">Assignments:</div>
                                    {referredAssignments
                                      ?.map<React.ReactNode>((assignment) => (
                                        <Link
                                          to={`/assignment/${assignment.assignmentKey}`}
                                          key={`referred-assignment-${assignment.assignmentKey}`}
                                        >
                                          {assignment.assignmentKey}
                                        </Link>
                                      ))
                                      .reduce((previous, current) => [
                                        previous,
                                        ', ',
                                        current,
                                      ])}
                                  </Col>
                                )}
                              </Row>
                            </div>
                          </BasePageCard>
                        </Col>
                      )}
                  </Row>
                </Col>

                {isAdmin && !!duplicates?.length && (
                  <Col xs={24} lg={12} className="d-flex">
                    <BasePageCard
                      title={
                        <span>
                          Original records as potential duplicates
                          <Tooltip
                            overlayClassName="light-tooltip"
                            title="If merged, this record in this box will be the Primary worker record as it was the original one."
                            trigger="click"
                            placement="topLeft"
                            getPopupContainer={(triggerNode) =>
                              triggerNode.parentNode as HTMLElement
                            }
                          >
                            <span
                              className={cx(
                                'icon icon-attention-filled',
                                styles.tooltipOpener,
                              )}
                            />
                          </Tooltip>
                        </span>
                      }
                      icons={
                        <span className="icon icon-potential-duplicates" />
                      }
                      className={cx('stretch-card', styles.collapseCard)}
                    >
                      <div className="scroll-box">
                        <BaseCollapse className={styles.collapse}>
                          {duplicates.map((duplicateWorker) => (
                            <PotentialDuplicateWorker
                              key={duplicateWorker.id}
                              workerId={worker?.id as string}
                              duplicateWorker={duplicateWorker}
                              isMergeDisabled={isButtonsDisabled}
                              disableButtons={setIsButtonsDisabled}
                            />
                          ))}
                        </BaseCollapse>
                      </div>
                    </BasePageCard>
                  </Col>
                )}
              </Row>
            </div>

            {isAdmin && (
              <ActivitiesDetailsSection
                entityId={worker?.id as string}
                pageType={ActivitiesSectionPageTypeEnum.Worker}
                scrollRef={containerRef}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
